import React, { useState } from "react";
import {
    isAlphaNumericKey,
    keyCodes,
    CellTemplate,
    Compatible,
    Uncertain,
    UncertainCompatible,
} from "@silevis/reactgrid";
import {
    Autocomplete,
    ClickAwayListener,
    Grid,
    ListItem,
    TextField,
} from "@mui/material";
import { formattedCurrency } from "@/utils";
import type { SheetCell } from "../../engine/cells";

// Removed legacy imports and replaced formatting correctly
const getLineItemRate = (
    existing: number,
    valContext: any,
    rateType: string,
) => {
    if (!valContext || !valContext.vendor_rate) return existing;
    switch (rateType) {
        case "Hours":
            return Math.round((valContext.vendor_rate / 8) * 100) / 100;
        case "Days":
        case "Percentage":
            return valContext.vendor_rate;
        case "Weeks":
            return valContext.vendor_rate * 5;
        default:
            return existing;
    }
};

export class DynamicRateCellTemplate implements CellTemplate {
    private options: any[];
    constructor(options: any[]) {
        this.options = options;
    }

    getClassName(cell: Compatible<any>) {
        return "dynamic-rate-cell";
    }

    getStyle(cell: Compatible<any>, isInEditMode: boolean) {
        return {
            overflow: "visible",
        };
    }

    getCompatibleCell(uncertainCell: Uncertain<any>): Compatible<any> {
        return {
            ...uncertainCell,
            text: uncertainCell.text || "",
            value: uncertainCell.value || 0,
        } as any;
    }

    handleKeyDown(
        cell: Compatible<any>,
        keyCode: number,
        ctrl: boolean,
        shift: boolean,
        alt: boolean,
    ): { cell: Compatible<any>; enableEditMode: boolean } {
        if ((!ctrl && !alt && isAlphaNumericKey(keyCode)) || keyCode === 42) {
            const char = String.fromCharCode(keyCode);
            const updatedCell = this.update(cell, {
                ...cell,
                text: char,
                value: parseFloat(char),
            } as any);
            return { cell: updatedCell, enableEditMode: true };
        }
        return {
            cell,
            enableEditMode:
                keyCode == keyCodes.POINTER || keyCode == keyCodes.ENTER,
        };
    }

    update(
        cell: Compatible<any>,
        cellToMerge: UncertainCompatible<any>,
    ): Compatible<any> {
        return this.getCompatibleCell({
            ...cell,
            text: cellToMerge.text,
            value: cellToMerge.value,
        });
    }

    render(
        cell: Compatible<any>,
        isInEditMode: boolean,
        onCellChanged: (cell: Compatible<any>, commit: boolean) => void,
    ) {
        const cellCurrency = cell.value?.currency || "CAD";
        const cellOptions = this.options
            .filter((option) => option.currency === cellCurrency)
            .map((option) => ({
                ...option,
                vendor_rate: option.internal_rate,
            }));
        return (
            <CustomDropdownCell
                cell={cell}
                options={cellOptions}
                onCommitCellChange={(newCellValue) => {
                    onCellChanged(
                        this.getCompatibleCell({
                            ...cell,
                            text: newCellValue,
                            value: {
                                ...newCellValue,
                                rate_type: cell.value.rate_type,
                            },
                        }),
                        true,
                    );
                }}
            />
        );
    }
}

const CustomDropdownCell = ({ cell, options, onCommitCellChange }: any) => {
    const [hover, setHover] = useState<boolean>(false);
    const handleMouseEnter = () => {
        setHover(true);
    };
    const handleMouseLeave = () => {
        setHover(false);
    };
    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
            }}
        >
            <CustomAutocomplete
                cell={cell}
                options={options}
                onCommitCellChange={onCommitCellChange}
            />
        </div>
    );
};

const CustomAutocomplete = ({ cell, options, onCommitCellChange }: any) => {
    const [editing, setEditing] = useState(false);
    const [selectedValue, setSelectedValue] = useState(null);

    const handleClickText = () => {
        if (!cell.nonEditable) {
            setEditing(true);
        }
    };

    const handleAutocompleteChange = (event, newValue) => {
        setEditing(false);
        setSelectedValue(newValue);
        onCommitCellChange(newValue);
    };

    const handleAutocompleteClear = (event, newValue, reason) => {
        if (reason == "clear") {
            onCommitCellChange(null);
            setSelectedValue(null);
        }
    };

    const handleInputKeyDown = (e) => {
        e.stopPropagation();
    };

    return (
        <Grid container alignItems="center" width="100%" height="100%">
            <Grid item width={"100%"}>
                <ClickAwayListener
                    onClickAway={() => {
                        setEditing(false);
                    }}
                >
                    {editing ? (
                        <Autocomplete
                            freeSolo
                            disableClearable
                            open
                            style={{ padding: 0 }}
                            value={selectedValue || cell.value}
                            sx={{ width: "100%" }}
                            disablePortal
                            options={options}
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option, { selected }) => {
                                return getOptionComponent(props, option);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    sx={{ border: "none", outline: "none" }}
                                    autoFocus
                                    {...params}
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        style: {
                                            fontSize: 12,
                                            border: "none",
                                            padding: 2,
                                            paddingLeft: 6,
                                        },
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: { padding: 0, fontSize: 12 },
                                    }}
                                />
                            )}
                            onKeyDown={handleInputKeyDown}
                            onChange={handleAutocompleteChange}
                            onInputChange={handleAutocompleteClear}
                            onPointerDown={(e: any) => {
                                e.stopPropagation();
                            }}
                            isOptionEqualToValue={(option: any, value: any) =>
                                option.id === value.id
                            }
                        />
                    ) : (
                        <div
                            style={{
                                textAlign: "right",
                                cursor: cell.nonEditable
                                    ? "default"
                                    : "pointer",
                            }}
                            onClick={handleClickText}
                        >
                            {formattedCurrency(
                                getLineItemRate(
                                    0,
                                    cell.value,
                                    cell.value.rate_type,
                                ),
                            )}
                        </div>
                    )}
                </ClickAwayListener>
            </Grid>
        </Grid>
    );
};

const getOptionComponent = (props: any, option: any) => {
    if (!option) {
        return <div style={{ minHeight: "20px", width: "100%" }} />;
    }

    return (
        <ListItem
            {...props}
            key={option.id}
            style={{ padding: 0, margin: 4, fontSize: 12 }}
        >
            <div style={{ minHeight: "20px", width: "100%" }}>
                {option.name}
            </div>
        </ListItem>
    );
};
